import { LAST_WRECK_CONTENT, type RoomId, type SalvageId } from '../content';
import {
  createInitialGameState,
  reduceGame,
  type GameCommand,
  type GameEvent,
  type GameState,
} from '../systems';
import type {
  RuntimeCommand,
  RuntimeStepContext,
  RuntimeSystemsBridge,
  RuntimeSystemsSnapshot,
  SystemsPresentationEvent,
} from './contracts';
import { WRECK_PASSAGES } from './wreckLayout';
import { AUTHORED_BREAK_POINTS } from './wreckLayout';

export interface ReducerSystemsBridgeOptions {
  readonly seed?: number;
  readonly onGameEvents?: (events: readonly GameEvent[], state: GameState) => void;
  readonly onStateChange?: (state: GameState) => void;
}

const roomIds = new Set<string>(LAST_WRECK_CONTENT.rooms.map((room) => room.id));
const salvageIds = new Set<string>(LAST_WRECK_CONTENT.salvage.map((salvage) => salvage.id));

function isRoomId(id: string): id is RoomId {
  return roomIds.has(id);
}

function isSalvageId(id: string): id is SalvageId {
  return salvageIds.has(id);
}

export function passageIdToCutPointId(passageId: string): string | null {
  return (
    AUTHORED_BREAK_POINTS.find((point) => point.id === passageId)?.cutPointId ??
    WRECK_PASSAGES.find((passage) => passage.id === passageId)?.cutPointId ??
    null
  );
}

export class ReducerSystemsBridge implements RuntimeSystemsBridge {
  private state: GameState;
  private readonly seed: number;
  private readonly onGameEvents:
    ((events: readonly GameEvent[], state: GameState) => void) | undefined;
  private readonly onStateChange: ((state: GameState) => void) | undefined;
  private paused = false;
  private tick = 0;
  private scannerActive = false;
  private cutterActive = false;
  private lastStateNotification = -1;
  private readonly presentationEvents: SystemsPresentationEvent[] = [];

  constructor(options: ReducerSystemsBridgeOptions = {}) {
    this.seed = options.seed ?? 0x1a57b00b;
    this.state = createInitialGameState(this.seed);
    this.onGameEvents = options.onGameEvents;
    this.onStateChange = options.onStateChange;
  }

  step(context: RuntimeStepContext): readonly RuntimeCommand[] {
    if (this.paused) return [];
    this.tick = context.tick;
    this.scannerActive = false;
    this.cutterActive = false;
    const presentation: RuntimeCommand[] = [];

    this.dispatch({ type: 'TICK', seconds: context.dtSeconds });
    this.enterObservedRoom(context.roomId);
    if (context.input.selectedTool !== this.state.selectedTool) {
      const events = this.dispatch({ type: 'SELECT_TOOL', tool: context.input.selectedTool });
      if (!events.some((event) => event.type === 'COMMAND_REJECTED')) {
        presentation.push({ type: 'select-tool', tool: context.input.selectedTool });
      }
    }

    const scanRequested =
      context.input.scannerPulsePressed ||
      (context.input.selectedTool === 'scanner' && context.input.toolPressed);
    if (scanRequested) {
      const radial = context.input.scannerPulsePressed;
      const events = this.dispatch({ type: 'SCAN', mode: radial ? 'radial' : 'forward' });
      if (events.some((event) => event.type === 'SCAN_PULSE')) {
        this.scannerActive = true;
        presentation.push({ type: 'scan', radial });
      }
    }

    if (context.input.selectedTool === 'cutter' && context.input.useTool) {
      this.cutterActive = true;
      const passageId = context.nearbyTarget?.kind === 'cut-point' ? context.nearbyTarget.id : null;
      const cutPointId = passageId ? passageIdToCutPointId(passageId) : null;
      if (cutPointId) {
        const aligned = context.nearbyTarget?.validForTool ?? false;
        const events = this.dispatch({
          type: 'CUT',
          cutPointId,
          seconds: context.dtSeconds,
          alignment: aligned ? 1 : 0,
        });
        if (events.some((event) => event.type === 'CUT_PROGRESS')) {
          presentation.push({ type: 'cut', targetId: passageId, aligned });
        }
      }
    }

    if (
      context.input.selectedTool === 'grapple' &&
      context.input.toolPressed &&
      context.nearbyTarget?.kind === 'salvage' &&
      isSalvageId(context.nearbyTarget.id)
    ) {
      const events = this.dispatch({
        type: 'GRAPPLE_ATTACH',
        salvageId: context.nearbyTarget.id,
        distance: context.nearbyTarget.distance,
        lineOfSight: true,
      });
      if (events.some((event) => event.type === 'GRAPPLE_ATTACHED')) {
        presentation.push({ type: 'grapple', targetId: context.nearbyTarget.id });
      }
    }

    if (this.state.grapple && context.grapple) {
      const events = this.dispatch({ type: 'GRAPPLE_UPDATE', ...context.grapple });
      if (events.some((event) => event.type === 'CABLE_BROKE')) {
        presentation.push({ type: 'release-grapple' });
      }
    }

    if (context.input.cancelPressed) {
      this.dispatch({ type: 'GRAPPLE_RELEASE' });
      presentation.push({ type: 'release-grapple' });
    }
    if (context.input.contextPressed) {
      if (context.nearbyTarget?.kind === 'power-node') {
        const bus = context.nearbyTarget.id.endsWith('-A') ? 'A' : 'B';
        this.dispatch({ type: 'SET_POWER', action: 'restore', bus });
      }
      presentation.push({ type: 'context', targetId: context.nearbyTarget?.id ?? null });
    }
    return presentation;
  }

  snapshot(): RuntimeSystemsSnapshot {
    return {
      tick: this.tick,
      selectedTool: this.state.selectedTool,
      scannerCooldown: this.state.scannerCooldownSeconds,
      scannerActive: this.scannerActive,
      cutterActive: this.cutterActive,
      grappleAttachedId: this.state.grapple?.salvageId ?? null,
      toolDisruptedSeconds: this.state.toolDisruptionSeconds,
      paused: this.paused,
    };
  }

  gameState(): GameState {
    return this.state;
  }

  observeRoom(roomId: string): void {
    this.enterObservedRoom(roomId);
  }

  setPaused(paused: boolean): void {
    this.paused = paused;
    this.scannerActive = false;
    this.cutterActive = false;
    this.lastStateNotification = -1;
    this.presentationEvents.splice(0);
  }

  reset(): void {
    this.state = createInitialGameState(this.seed);
    this.tick = 0;
    this.paused = false;
    this.scannerActive = false;
    this.cutterActive = false;
    this.lastStateNotification = -1;
    this.presentationEvents.splice(0);
  }

  tryDepositSalvage(targetId: string): boolean {
    if (!isSalvageId(targetId) || this.state.grapple?.salvageId !== targetId) return false;
    const events = this.dispatch({ type: 'DEPOSIT', salvageId: targetId });
    return events.some((event) => event.type === 'SALVAGE_BANKED');
  }

  tryAttachSalvage(targetId: string, distance: number): boolean {
    if (!isSalvageId(targetId) || !this.canTargetSalvage(targetId)) return false;
    if (this.state.selectedTool !== 'grapple') {
      this.dispatch({ type: 'SELECT_TOOL', tool: 'grapple' });
    }
    const events = this.dispatch({
      type: 'GRAPPLE_ATTACH',
      salvageId: targetId,
      distance,
      lineOfSight: true,
    });
    return events.some((event) => event.type === 'GRAPPLE_ATTACHED');
  }

  advanceTargetedCut(
    targetId: string,
    seconds: number,
  ): { readonly progress: number; readonly complete: boolean } | null {
    const cutPointId = passageIdToCutPointId(targetId);
    if (!cutPointId || !Number.isFinite(seconds) || seconds <= 0) return null;
    if (this.state.selectedTool !== 'cutter') {
      const selection = this.dispatch({ type: 'SELECT_TOOL', tool: 'cutter' });
      if (selection.some((event) => event.type === 'COMMAND_REJECTED')) return null;
    }
    const events = this.dispatch({ type: 'CUT', cutPointId, seconds, alignment: 1 });
    if (events.some((event) => event.type === 'COMMAND_REJECTED')) return null;
    const cut = this.state.cuts[cutPointId];
    return cut ? { progress: cut.progress, complete: cut.complete } : null;
  }

  notifyGrappleReleased(reason: 'cancel' | 'overload' | 'deposit'): void {
    if (reason === 'deposit' || !this.state.grapple) return;
    this.dispatch({ type: 'GRAPPLE_RELEASE' });
  }

  isPassageOpen(passageId: string): boolean {
    return this.state.connections[passageId]?.open ?? false;
  }

  canTargetSalvage(targetId: string): boolean {
    return (
      isSalvageId(targetId) &&
      !this.state.salvage[targetId].banked &&
      this.state.salvage[targetId].roomId === this.state.currentRoomId
    );
  }

  dispatchGameCommand(command: GameCommand): readonly GameEvent[] {
    return this.dispatch(command);
  }

  notifyHardImpact(severity: number): void {
    this.dispatch({ type: 'HARD_IMPACT', severity });
  }

  drainPresentationEvents(): readonly SystemsPresentationEvent[] {
    return this.presentationEvents.splice(0);
  }

  private enterObservedRoom(roomId: string | null): void {
    if (!roomId || !isRoomId(roomId) || roomId === this.state.currentRoomId) return;
    const connection = LAST_WRECK_CONTENT.connections.find(
      (candidate) =>
        (candidate.from === this.state.currentRoomId && candidate.to === roomId) ||
        (candidate.to === this.state.currentRoomId && candidate.from === roomId),
    );
    if (!connection) return;
    const events = this.dispatch({ type: 'ENTER_ROOM', roomId, viaConnectionId: connection.id });
    if (!events.some((event) => event.type === 'COMMAND_REJECTED') && this.state.grapple) {
      this.dispatch({ type: 'MOVE_GRAPPLED_SALVAGE', roomId });
    }
  }

  private dispatch(command: GameCommand): readonly GameEvent[] {
    const result = reduceGame(this.state, command);
    this.state = result.state;
    for (const event of result.events) {
      if (event.type === 'PRESSURE_EQUALIZED') {
        this.presentationEvents.push({
          type: 'pressure-equalized',
          from: event.from,
          to: event.to,
          impulse: event.impulse,
        });
      }
      if (event.type === 'CUT_COMPLETE') {
        this.presentationEvents.push({ type: 'cut-complete', cutPointId: event.cutPointId });
      }
      if (event.type === 'INSTABILITY_THRESHOLD') {
        this.presentationEvents.push({
          type: 'instability-threshold',
          threshold: event.threshold,
        });
      }
      if (event.type === 'POWER_CHANGED') {
        this.presentationEvents.push({
          type: 'power-changed',
          bus: event.bus,
          state: event.state,
        });
      }
    }
    const stateNotification = Math.floor(this.state.elapsedSeconds * 10);
    if (stateNotification !== this.lastStateNotification || this.state.phase === 'ended') {
      this.lastStateNotification = stateNotification;
      this.onStateChange?.(this.state);
    }
    if (result.events.length > 0) this.onGameEvents?.(result.events, this.state);
    return result.events;
  }
}
